<template>
    <div class="w-100 align-self-start">
        <v-card flat color="transparent">
            <v-card-title class="d-block">
                <v-btn class="mb-3" small text color="secondary" :to="{ name: 'user-pools' }">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Resource Pools
                </v-btn>
                <div>Create account</div>
            </v-card-title>

            <v-card-text>
                <v-card flat style="max-width: 600px">
                    <v-card-text>
                        <v-form ref="form" v-model="formValid" @submit.prevent="submit">
                            <v-text-field
                                v-model="name"
                                label="Account name"
                                outlined
                                dense
                                :rules="[rules.required]"
                                :disabled="saving"
                                required></v-text-field>
                            <ResourcePoolSelector
                                v-model="parentResourcePoolId"
                                :items="parentPoolOptions"
                                label="Parent resource pool"
                                hint="Leave empty to create a root account"
                                persistentHint
                                clearable
                                :loading="loadingPools"
                                :disabled="saving || loadingPools" />
                            <template v-if="hasParent">
                                <v-checkbox
                                    v-model="overrideDates"
                                    label="Override inherited start and end dates"
                                    hint="By default, dates are inherited from the parent resource pool."
                                    persistent-hint
                                    :disabled="saving" />
                                <v-row class="mt-2">
                                    <v-col cols="12" sm="6">
                                        <v-menu v-model="startDateMenu" :close-on-content-click="false" offset-y min-width="auto" :disabled="datesDisabled">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="displayedStartDate"
                                                    label="Start date"
                                                    outlined
                                                    dense
                                                    readonly
                                                    :loading="loadingParentDates"
                                                    :rules="overrideDates ? [rules.required] : []"
                                                    :disabled="datesDisabled"
                                                    v-bind="attrs"
                                                    v-on="on" />
                                            </template>
                                            <v-date-picker
                                                v-model="displayedStartDate"
                                                :max="displayedEndDate || undefined"
                                                :disabled="datesDisabled"
                                                @input="startDateMenu = false" />
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-menu v-model="endDateMenu" :close-on-content-click="false" offset-y min-width="auto" :disabled="datesDisabled">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="displayedEndDate"
                                                    label="End date"
                                                    outlined
                                                    dense
                                                    readonly
                                                    :loading="loadingParentDates"
                                                    :rules="overrideDates ? [rules.required, validDateRange] : []"
                                                    :disabled="datesDisabled"
                                                    v-bind="attrs"
                                                    v-on="on" />
                                            </template>
                                            <v-date-picker
                                                v-model="displayedEndDate"
                                                :min="displayedStartDate || undefined"
                                                :disabled="datesDisabled"
                                                @input="endDateMenu = false" />
                                        </v-menu>
                                    </v-col>
                                </v-row>
                                <v-alert v-if="parentDatesError" type="error" text dense>{{ parentDatesError }}</v-alert>
                            </template>
                            <v-divider class="my-5"></v-divider>
                            <div class="text-subtitle-2 mb-2">Services</div>
                            <v-row>
                                <v-col cols="12" sm="4">
                                    <v-checkbox v-model="services.hpc" label="HPC" dense hide-details :disabled="saving"></v-checkbox>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-checkbox v-model="services.database" label="Database" dense hide-details :disabled="saving"></v-checkbox>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-checkbox v-model="services.vimeo" label="Video library" dense hide-details :disabled="saving"></v-checkbox>
                                </v-col>
                            </v-row>
                            <div class="d-flex justify-end mt-6">
                                <v-btn :disabled="!formValid || saving" :loading="saving" color="primary" text type="submit">Create</v-btn>
                            </div>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import ResourcePoolSelector from '@/modules/user/components/ResourcePoolSelector.vue'
import { RESOURCE_POOL_API_TYPE } from '@/modules/user/components/resourcePoolMeta'

export default {
    name: 'UserPoolsCreateAccount',
    components: { ResourcePoolSelector },
    data() {
        return {
            formValid: false,
            saving: false,
            loadingPools: false,
            name: '',
            parentResourcePoolId: null,
            overrideDates: false,
            inheritedStartDate: null,
            inheritedEndDate: null,
            loadingParentDates: false,
            parentDatesError: null,
            parentDatesRequestId: 0,
            startDate: null,
            endDate: null,
            startDateMenu: false,
            endDateMenu: false,
            services: { hpc: false, database: false, vimeo: false },
            rules: {
                required: value => Boolean(String(value || '').trim()) || 'Field is required'
            }
        }
    },
    computed: {
        ...mapState('userStore', ['availableResourcePools']),
        parentPoolOptions() {
            return this.availableResourcePools
        },
        hasParent() {
            return this.parentResourcePoolId !== null && this.parentResourcePoolId !== undefined
        },
        datesDisabled() {
            return this.saving || !this.overrideDates
        },
        displayedStartDate: {
            get() {
                return this.overrideDates && this.startDate !== null ? this.startDate : this.inheritedStartDate
            },
            set(value) {
                this.startDate = value
            }
        },
        displayedEndDate: {
            get() {
                return this.overrideDates && this.endDate !== null ? this.endDate : this.inheritedEndDate
            },
            set(value) {
                this.endDate = value
            }
        }
    },
    watch: {
        overrideDates() {
            this.startDateMenu = false
            this.endDateMenu = false
        },
        parentResourcePoolId() {
            this.overrideDates = false
            this.startDate = null
            this.endDate = null
            this.startDateMenu = false
            this.endDateMenu = false
            this.fetchParentDates()
        }
    },
    methods: {
        async fetchParentDates() {
            const parentId = this.parentResourcePoolId
            const requestId = ++this.parentDatesRequestId
            this.inheritedStartDate = null
            this.inheritedEndDate = null
            this.parentDatesError = null
            this.loadingParentDates = this.hasParent
            if (!this.hasParent) return

            try {
                const { data } = await this.$axios.get(`/resource/${parentId}/status`)
                if (this.parentDatesRequestId !== requestId) return
                this.inheritedStartDate = data.resource_pool_start
                this.inheritedEndDate = data.resource_pool_end
            } catch (error) {
                if (this.parentDatesRequestId !== requestId) return
                this.parentDatesError = 'Could not load inherited dates.'
            } finally {
                if (this.parentDatesRequestId === requestId) this.loadingParentDates = false
            }
        },
        validDateRange(value) {
            return !this.displayedStartDate || !value || value >= this.displayedStartDate || 'End date must be on or after start date'
        },
        async fetchPools() {
            this.loadingPools = true
            try {
                const { data } = await this.$axios.get('/resource/available_rps')
                this.$store.commit('userStore/setAvailableResourcePoolData', data)
            } finally {
                this.loadingPools = false
            }
        },
        async submit() {
            if (!this.$refs.form || !this.$refs.form.validate()) {
                return
            }

            this.saving = true
            try {
                const payload = {
                    long_id: this.name.trim(),
                    resource_pool_type: RESOURCE_POOL_API_TYPE.ACCOUNT,
                    hpc_enabled: this.services.hpc,
                    db_enabled: this.services.database,
                    vimeo_enabled: this.services.vimeo
                }
                if (this.hasParent) {
                    payload.parent_rpid = Number(this.parentResourcePoolId)
                    if (this.overrideDates) {
                        payload.rp_start = `${this.displayedStartDate}T00:00:00`
                        payload.rp_end = `${this.displayedEndDate}T00:00:00`
                    }
                }
                await this.$axios.post('/resource_pools', payload)
                await this.$store.dispatch('userStore/fetchAvailableResourcePoolData')
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Account created.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
                this.$router.push({ name: 'user-pools' })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: error.response?.data?.debug || 'Could not create account.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            } finally {
                this.saving = false
            }
        }
    },
    created() {
        this.fetchPools()
    }
}
</script>
